// src/components/ExamsTable.tsx
import React, { useState } from 'react';
import { Edit, Trash2, Users, UserPlus } from 'lucide-react';
import { Exam } from '../types';

interface ExamsTableProps {
  exams: Exam[];
  onDelete: (id: number) => Promise<void>;
  onEdit: (exam: Exam) => void;
  onAssign?: (examId: number) => void;
}

const ExamsTable: React.FC<ExamsTableProps> = ({ exams, onDelete, onEdit, onAssign }) => {
  if (!exams || !Array.isArray(exams)) {
    return <div>No exams available</div>;
  }

  return (
    <div className="bg-gray-800 rounded-lg shadow-lg overflow-hidden mb-8">
      <table className="w-full">
        <thead className="bg-gray-700">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Name</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Type</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Time/Pass</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Questions</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Assignment</th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-700">
          {exams.map((exam) => (
            <tr key={exam.id}>
              <td className="px-6 py-4 whitespace-nowrap">
                <div>
                  <div className="font-medium">{exam.name}</div>
                  {exam.exam_type === 'final' && exam.max_attempts && (
                    <div className="text-sm text-orange-400">
                      Max {exam.max_attempts} attempt{exam.max_attempts !== 1 ? 's' : ''}
                    </div>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <span className={`px-2 py-1 rounded-full text-xs ${
                  exam.exam_type === 'final' 
                    ? 'bg-red-500/20 text-red-300' 
                    : 'bg-green-500/20 text-green-300'
                }`}>
                  {exam.exam_type === 'final' ? 'Final' : 'Practice'}
                </span>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="text-sm">
                  <div>{exam.time_limit} min</div>
                  <div className="text-gray-400">{exam.pass_score}% pass</div>
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">{exam.questions?.length || 0}</td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex items-center">
                  {exam.is_assigned_to_all ? (
                    <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-300 rounded-full flex items-center">
                      <Users className="h-3 w-3 mr-1" />
                      All Users
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs bg-gray-500/20 text-gray-400 rounded-full">
                      Specific Users
                    </span>
                  )}
                  {onAssign && !exam.is_assigned_to_all && (
                    <button
                      onClick={() => onAssign(exam.id)}
                      className="ml-2 text-blue-400 hover:text-blue-300"
                      title="Assign to Users"
                    >
                      <UserPlus className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </td>
              <td className="px-6 py-4 whitespace-nowrap">
                <div className="flex space-x-2">
                  <button
                    onClick={() => onEdit(exam)}
                    className="text-blue-400 hover:text-blue-300"
                    title="Edit Exam"
                  >
                    <Edit className="h-5 w-5" />
                  </button>
                  <button 
                    onClick={() => onDelete(exam.id)}
                    className="text-red-400 hover:text-red-300"
                    title="Delete Exam"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ExamsTable;